import { scopeThreadRef, scopedThreadKey } from "@amoreg/client-runtime/environment";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import {
  sortActiveThreadsByOrderKey,
  sortPinnedThreadsByOrderKey,
} from "@amoreg/client-runtime/state/thread-sort";
import {
  applySidebarThreadDrop,
  sortSettledThreadsForSidebar,
  type SidebarSection,
  type SidebarThreadDropPlan,
} from "./Sidebar.logic";

export const sidebarThreadKey = (thread: EnvironmentThreadShell) =>
  scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));
export type SidebarThreadGroups = Record<SidebarSection, readonly EnvironmentThreadShell[]>;
export type SidebarThreadMove = {
  key: string;
  section: "pinned" | "active" | "settled";
  occurredAt: string;
  assigned: ReadonlyMap<string, string>;
  unsettle: boolean;
};
export function sidebarDropAssignments(
  plan: Exclude<SidebarThreadDropPlan, { kind: "none" }>,
  key: string,
): ReadonlyMap<string, string> {
  return new Map(
    (plan.kind === "settle"
      ? []
      : plan.kind === "pin"
        ? [
            ...plan.extraAssignments,
            ...(plan.orderKey ? [{ id: key, orderKey: plan.orderKey }] : []),
          ]
        : plan.assignments
    ).map(({ id, orderKey }) => [id, orderKey]),
  );
}

/** Preview and pending rows use exactly the same global comparators as committed rows. */
export function projectSidebarThreadGroups(
  groups: SidebarThreadGroups,
  move: SidebarThreadMove,
): SidebarThreadGroups {
  const moved = Object.values(groups)
    .flat()
    .find((thread) => sidebarThreadKey(thread) === move.key);
  if (!moved) return groups;
  const next = Object.fromEntries(
    Object.entries(groups).map(([section, rows]) => [
      section,
      rows
        .filter((thread) => sidebarThreadKey(thread) !== move.key)
        .map((thread) => {
          const orderKey = move.assigned.get(sidebarThreadKey(thread));
          return orderKey === undefined
            ? thread
            : {
                ...thread,
                [move.section === "pinned" ? "pinOrderKey" : "activeOrderKey"]: orderKey,
              };
        }),
    ]),
  ) as Record<SidebarSection, EnvironmentThreadShell[]>;
  next[move.section].push(
    applySidebarThreadDrop(
      moved,
      move.section,
      move.occurredAt,
      move.assigned.get(move.key),
      move.unsettle,
    ),
  );
  next.pinned = sortPinnedThreadsByOrderKey(next.pinned);
  next.active = sortActiveThreadsByOrderKey(next.active);
  next.settled = sortSettledThreadsForSidebar(next.settled);
  return next;
}
